import $ from 'jquery';
import {convertPayPalAddressToCart, extractFullName, setFieldValue} from '@ppcp/utils';
import BaseGateway from './BaseGateway';

/**
 * Abstract PayPal Gateway
 *
 * Base class for PayPal-based payment gateways (PayPal, Credit Card, Google Pay, etc.)
 * Provides common functionality for vault setup tokens, checkout data extraction,
 * and approval handling.
 */
class AbstractPayPalGateway extends BaseGateway {

    /**
     * Handle payment approval - common flow for vault setup and direct payments
     * Can be overridden by subclasses for custom behavior
     *
     * @param {Object} data - PayPal approval data
     * @param {Object} actions - PayPal actions object (optional for some flows)
     * @returns {Promise} Promise that resolves when approval is complete
     */
    async onApprove(data, actions = null) {
        // Set the order ID field if present
        if (data.orderID) {
            setFieldValue(this.getOrderFieldKey(), data.orderID, null);
        }

        // Handle vault setup flow
        if (data.vaultSetupToken) {
            return this.handleVaultSetupToken(data);
        } else {
            // Handle direct payment flow
            return this.handleDirectPayment(data, actions);
        }
    }

    /**
     * Handle vault setup token creation and submission
     * @param {Object} data - PayPal approval data with vaultSetupToken
     * @returns {Promise} Promise that resolves when vault token is handled
     */
    async handleVaultSetupToken(data) {
        try {
            const {payment_token: token, nonce} = await this.controller.createPaymentToken(data.vaultSetupToken, {
                order_id: data.orderID
            });

            // Extract and normalize token data before passing to controller
            const checkoutData = this.extractTokenCheckoutData(token);

            // Set payment token field
            setFieldValue(`${this.id}_payment_token`, token.id, null);
            setFieldValue(`${this.id}_payment_token_nonce`, nonce, null);

            // Submit with checkout data
            this.controller.submit(checkoutData);
        } catch (error) {
            this.controller.submitError(error);
        }
    }

    /**
     * Handle direct payment flow (no vault setup)
     * Gets order details from PayPal and submits with standardized checkout data
     * @param {Object} data - PayPal approval data
     * @param {Object} actions - PayPal actions object
     * @returns {Promise} Promise that resolves when payment is handled
     */
    async handleDirectPayment(data, actions) {
        try {
            let standardizedData = {};
            if (actions && actions.order) {
                const response = await this.getOrder(data, actions);
                standardizedData = this.extractStandardizedCheckoutData(response);
            }
            this.controller.submit(standardizedData);
        } catch (error) {
            this.controller.submitError(error);
        }
    }

    /**
     * Get the approved PayPal order. Falls back to the server if actions.order.get() fails,
     * e.g. PayPal returning NOT_AUTHORIZED for some Venmo orders.
     * @param {Object} data - PayPal approval data
     * @param {Object} actions - PayPal actions object
     * @returns {Promise<Object>} PayPal order response
     */
    async getOrder(data, actions) {
        try {
            return await actions.order.get();
        } catch (error) {
            if (!data.orderID) {
                throw error;
            }
            return this.controller.fetchPayPalOrder(data.orderID);
        }
    }

    /**
     * Extract standardized checkout data from PayPal order response
     * @param {Object} response - PayPal order response from actions.order.get()
     * @returns {Object} Standardized checkout data with billing and shipping properties
     */
    extractStandardizedCheckoutData(response) {
        const data = {};

        // Extract billing data from payer
        if (response?.payer) {
            let billing = {};

            // Add address fields
            if (response.payer.address) {
                const address = convertPayPalAddressToCart(response.payer.address);
                billing = {...address};
            }

            // Add name fields
            if (response.payer.name) {
                billing.first_name = response.payer.name.given_name;
                billing.last_name = response.payer.name.surname;
            }

            // Add email
            if (response.payer.email_address) {
                billing.email = response.payer.email_address;
            }

            // Add phone
            if (response.payer.phone?.phone_number?.national_number) {
                billing.phone = response.payer.phone.phone_number.national_number;
            }

            data.billing = billing;
        }

        // Extract shipping data from purchase_units
        if (response?.purchase_units?.[0]?.shipping) {
            let shipping = {};
            const shippingData = response.purchase_units[0].shipping;

            // Add address fields
            if (shippingData.address) {
                const address = convertPayPalAddressToCart(shippingData.address);
                shipping = {...address};
            }

            // Add name fields
            if (shippingData.name?.full_name) {
                const name = extractFullName(shippingData.name.full_name);
                shipping.first_name = name[0];
                shipping.last_name = name[1];
            }

            data.shipping = shipping;
        }

        return data;
    }

    /**
     * Extract standardized checkout data from PayPal vault token
     * @param {Object} token - PayPal vault token object
     * @returns {Object} Standardized checkout data with billing and shipping properties
     */
    extractTokenCheckoutData(token) {
        const data = {};

        // Extract billing data from token.payment_source.paypal
        if (token?.payment_source?.paypal) {
            let billing = {};
            const paypal = token.payment_source.paypal;

            // Add name fields
            if (paypal.name) {
                billing.first_name = paypal.name.given_name || '';
                billing.last_name = paypal.name.surname || '';
            }

            // Add email
            if (paypal.email_address) {
                billing.email = paypal.email_address;
            }

            // Add phone
            if (paypal.phone?.phone_number?.national_number) {
                billing.phone = paypal.phone.phone_number.national_number;
            }

            data.billing = billing;

            // Extract shipping data from token.payment_source.paypal.shipping
            if (paypal.shipping?.address) {
                let shipping = {};
                const shippingData = paypal.shipping;

                // Add address fields
                const address = convertPayPalAddressToCart(shippingData.address);
                shipping = {...address};

                // Add name fields
                if (shippingData.name?.full_name) {
                    const name = extractFullName(shippingData.name.full_name);
                    shipping.first_name = name[0];
                    shipping.last_name = name[1];
                }

                data.shipping = shipping;
            }
        }

        // Handle credit card tokens - extract from token.payment_source.card
        if (token?.payment_source?.card) {
            let billing = {};
            const card = token.payment_source.card;

            // Add name from card
            if (card.name) {
                const name = extractFullName(card.name);
                billing.first_name = name[0];
                billing.last_name = name[1];
            }

            // Add billing address from card
            if (card.billing_address) {
                const address = convertPayPalAddressToCart(card.billing_address);
                billing = {...billing, ...address};
            }

            data.billing = billing;
        }

        return data;
    }

    /**
     *
     * @param {Object} data
     * @param {String} source
     */
    onCancel(data, source) {
        this.orderId = data?.orderID || null;
        $(document.body).triggerHandler('wc_ppcp_on_cancel', [this, source, data]);
        this.controller.onCancel();
    }

    /**
     * Handle errors from PayPal
     * @param {Error} error - Error object
     * @param {string} source - Funding source (optional)
     */
    onError(error, source = null) {
        this.controller.onError(error, source);
    }

    onDestroy(source) {
        $(document.body).triggerHandler('wc_ppcp_on_destroy', [this.controller, source]);
    }
}

export {AbstractPayPalGateway};
export default AbstractPayPalGateway;